import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/axios';import { toast } from 'react-toastify';
import Header from '../../layouts/Header';
import { ArrowLeft, FileEarmarkSpreadsheet } from 'react-bootstrap-icons';
import { Spinner } from 'react-bootstrap';
import OutletSelect from '../../components/OutletSelect';

/* ─── styles ─────────────────────────────────────────────────────────────── */
const s = {
    page: { fontFamily: "'Inter', system-ui, sans-serif" },

    topbar: {
        background: '#1a1f36',
        borderRadius: 6,
        padding: '12px 16px',
        color: '#fff',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    tbEye: { fontSize: 9, fontWeight: 500, letterSpacing: 1.5, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', marginBottom: 2 },
    tbTitle: { fontSize: 14, fontWeight: 500, color: '#fff' },
    tbSub: { fontSize: 11, color: 'rgba(255,255,255,0.45)', marginTop: 1 },
    backBtn: {
        background: 'rgba(255,255,255,0.1)', border: '0.5px solid rgba(255,255,255,0.18)',
        color: '#fff', borderRadius: 8, fontSize: 11, fontWeight: 500,
        padding: '5px 11px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
    },

    filterBar: {
        background: '#fff', border: '0.5px solid #e5e7eb', borderRadius: 6,
        padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
    },
    filterLabel: { fontSize: 11, color: '#6b7280', fontWeight: 500, whiteSpace: 'nowrap' },
    genBtn: {
        background: '#1a1f36', color: '#fff', border: 'none', borderRadius: 6,
        padding: '6px 16px', fontSize: 12.5, fontWeight: 500, cursor: 'pointer',
        height: 34, display: 'flex', alignItems: 'center', gap: 5,
    },

    statsRow: { display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 2 },
    stat: (accent) => ({
        background: '#fff', border: '0.5px solid #e5e7eb', borderRadius: 8,
        padding: '8px 12px', borderLeft: `3px solid ${accent}`,
        flex: 1, minWidth: 80,
    }),
    statNum: (accent) => ({ fontSize: 20, fontWeight: 500, lineHeight: 1, color: accent }),
    statLabel: { fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.3, color: '#6b7280', marginTop: 2 },
    statSub: { fontSize: 10, color: '#9ca3af', marginTop: 1 },

    tableCard: { background: '#fff', border: '0.5px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' },
    tableHdr: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '10px 14px', borderBottom: '0.5px solid #f3f4f6', flexWrap: 'wrap', gap: 8,
    },
    tableTitle: { fontSize: 13, fontWeight: 500, color: '#1a1f36' },
    tableSub: { fontSize: 11, color: '#6b7280', marginTop: 1 },
    actions: { display: 'flex', gap: 6, alignItems: 'center' },

    pill: (active) => ({
        fontSize: 11, fontWeight: 500, padding: '4px 10px', borderRadius: 20,
        border: active ? 'none' : '0.5px solid #d1d5db',
        background: active ? '#1a1f36' : '#f9fafb',
        color: active ? '#fff' : '#6b7280', cursor: 'pointer',
    }),
    exportBtn: {
        background: '#f0fdf4', border: '0.5px solid #86efac', color: '#15803d',
        borderRadius: 8, padding: '4px 10px', fontSize: 11, fontWeight: 500,
        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
    },

    thead: { background: '#1a1f36' },
    th: {
        padding: '7px 10px', textAlign: 'left', fontSize: 10, fontWeight: 500,
        textTransform: 'uppercase', letterSpacing: 0.6, color: 'rgba(255,255,255,0.65)',
        whiteSpace: 'nowrap', border: 'none',
    },
    catRow: { background: '#f8f9ff' },
    catCell: {
        padding: '5px 10px', fontSize: 11, fontWeight: 500,
        color: '#2d3561', borderBottom: '0.5px solid #e5e7eb',
    },
    catBadge: {
        display: 'inline-block', background: '#1a1f36', color: '#fff',
        borderRadius: 20, padding: '0 7px', fontSize: 10, fontWeight: 500, marginLeft: 6,
    },
    td: { padding: '8px 10px', borderBottom: '0.5px solid #f3f4f6', verticalAlign: 'middle', fontSize: 12 },
    prodName: { fontWeight: 500, fontSize: 12, color: '#1a1f36', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 240 },
    desc: { color: '#6b7280', fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 280 },
    tableFooter: { padding: '7px 14px', borderTop: '0.5px solid #f3f4f6', fontSize: 10, color: '#9ca3af' },

    emptyState: { textAlign: 'center', padding: '40px 24px', color: '#9ca3af' },
};

/* ─── badge helpers ──────────────────────────────────────────────────────── */
const CLASS_MAP = {
    FOOD:     { bg: '#e6f1fb', color: '#0c447c', label: 'Food' },
    BEVERAGE: { bg: '#eeedfe', color: '#3c3489', label: 'Bev' },
    COMBOS:   { bg: '#faeeda', color: '#633806', label: 'Combo' },
    DESSERTS: { bg: '#fbeaf0', color: '#72243e', label: 'Dessert' },
    EXTRAS:   { bg: '#eaf3de', color: '#27500a', label: 'Extra' },
};

const ClassBadge = ({ type }) => {
    const t = CLASS_MAP[type] || { bg: '#f3f4f6', color: '#6b7280', label: type };
    return (
        <span style={{ background: t.bg, color: t.color, borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 500 }}>
            {t.label}
        </span>
    );
};

const FoodBadge = ({ type }) => {
    const isVeg = type === 'VEG' || type === 'VEGAN';
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 3,
            background: isVeg ? '#eaf3de' : '#faece7',
            color: isVeg ? '#27500a' : '#712b13',
            borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 500,
        }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: isVeg ? '#639922' : '#d85a30', flexShrink: 0 }} />
            {isVeg ? 'Veg' : 'Non-veg'}
        </span>
    );
};

/* ─── stat card ──────────────────────────────────────────────────────────── */
const StatCard = ({ label, value, accent, sub }) => (
    <div style={s.stat(accent)}>
        <div style={s.statNum(accent)}>{value}</div>
        <div style={s.statLabel}>{label}</div>
        {sub && <div style={s.statSub}>{sub}</div>}
    </div>
);

/* ─── compute stats ──────────────────────────────────────────────────────── */
const getStats = (data) => {
    let total = 0, veg = 0, nonVeg = 0, food = 0, beverage = 0, combos = 0, desserts = 0;
    data.forEach(cat => cat.products.forEach(p => {
        total++;
        if (p.foodType === 'VEG' || p.foodType === 'VEGAN') veg++; else nonVeg++;
        if (p.classType === 'FOOD') food++;
        else if (p.classType === 'BEVERAGE') beverage++;
        else if (p.classType === 'COMBOS') combos++;
        else if (p.classType === 'DESSERTS') desserts++;
    }));
    return { total, veg, nonVeg, food, beverage, combos, desserts, categories: data.length };
};

/* ═══════════════════════════════════════════════════════════════════════════ */
const ProductStockReport = () => {
    const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
    const [skin, setSkin] = useState(currentSkin);
    const navigate = useNavigate();
    const accessToken = localStorage.getItem('authToken');

    const [outlets, setOutlets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedOutlet, setSelectedOutlet] = useState('');
    const [selectedOutletName, setSelectedOutletName] = useState('');
    const [reportData, setReportData] = useState([]);
    const [outletName, setOutletName] = useState('');
    const [downloadBtnLoading, setDownloadBtnLoading] = useState(false);
    const [filterType, setFilterType] = useState('ALL');

    const isDark = skin === 'dark';

    /* ── dark mode overrides ── */
    const dm = (light, dark) => isDark ? dark : light;

    const getOutlets = async () => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const res = await axiosInstance.get('outlet', { headers });
            if (res?.data?.status === true) {
                setOutlets(res.data.data.outlets.filter(o => o?.status === 'ACTIVE' || 'INACTIVE'));
            }
        } catch {
            toast.error('Failed to fetch outlets', { position: 'top-right', autoClose: 3000, theme: 'colored' });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedOutlet) return;
        setLoading(true);
        try { await getProductStock(selectedOutlet); } finally { setLoading(false); }
    };

    const getProductStock = async (outletId, download) => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            let url = `product/getExhaustedProducts?outlet=${outletId?.value}`;
            if (download) url += `&isDownload=${download}`;
            const res = await axiosInstance.get(url, { headers, responseType: download ? 'blob' : 'json' });

            if (download) {
                const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                link.download = `${selectedOutletName} Out of Stock Report.xlsx`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(link.href);
                toast.success('Excel downloaded!', { position: 'top-right', autoClose: 3000, theme: 'light' });
            } else {
                setReportData(res.data.data.exhaustedProducts);
                const outlet = outlets.find(o => o._id === outletId);
                setOutletName(outlet?.name || selectedOutletName || 'Selected Outlet');
                setFilterType('ALL');
                toast.success(res.data.data.message, { position: 'top-right', autoClose: 2000, theme: 'colored' });
            }
        } catch (error) {
            let msg = 'Something went wrong';
            if (error?.response) {
                const d = error.response.data;
                msg = d?.data?.message || d?.data?.error?.message || d?.title || msg;
            }
            setReportData([]);
            toast.error(msg, { position: 'top-right', autoClose: 3000, theme: 'light' });
        } finally {
            setDownloadBtnLoading(false);
        }
    };

    const handleExport = () => {
        setDownloadBtnLoading(true);
        getProductStock(selectedOutlet, true);
    };

    useEffect(() => { getOutlets(); }, []);

    /* ── filtered data ── */
    const filteredData = reportData
        .map(cat => ({
            ...cat,
            products: cat.products.filter(p => {
                if (filterType === 'VEG') return p.foodType === 'VEG' || p.foodType === 'VEGAN';
                if (filterType === 'NON-VEG') return p.foodType === 'NON-VEG';
                return true;
            }),
        }))
        .filter(cat => cat.products.length > 0);

    const stats = reportData.length > 0 ? getStats(reportData) : null;
    const filteredTotal = filteredData.reduce((a, c) => a + c.products.length, 0);

    const pageStyle = {
        ...s.page,
        minHeight: '100vh',
        background: dm('#f5f6fa', '#0f1117'),
    };

    const cardStyle = (extra = {}) => ({
        ...extra,
        background: dm('#fff', '#1c2033'),
        border: `0.5px solid ${dm('#e5e7eb', '#2a2f4a')}`,
    });

    return (
        <React.Fragment>
            <Header onSkin={setSkin} />
            <div className="main main-app p-3 p-lg-4" style={pageStyle}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

                    {/* top bar */}
                    <div style={s.topbar}>
                        <div>
                            <div style={s.tbEye}>Reports</div>
                            <div style={s.tbTitle}>⚠️ Out of stock products</div>
                            <div style={s.tbSub}>Items currently exhausted at your outlet</div>
                        </div>
                        <button style={s.backBtn} onClick={() => navigate('/reports/all-reports')}>
                            <ArrowLeft size={12} /> Back to reports
                        </button>
                    </div>

                    {/* filter bar */}
                    <div style={cardStyle({ ...s.filterBar, padding: '10px 14px' })}>
                        {/* <span style={s.filterLabel}>Outlet</span> */}
                        <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 10, flex: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                            <div style={{ flex: '1 1 180px', maxWidth: 280 }}>
                                <OutletSelect
                                    value={selectedOutlet}
                                    onChange={(opt) => {
                                        setSelectedOutlet(opt);
                                        setSelectedOutletName(opt ? opt.label : '');
                                    }}
                                    required
                                />
                            </div>
                            <div>
                                <label className='text-white'>.</label>
                                <button
                                    type="submit"
                                    style={{ ...s.genBtn, opacity: (!selectedOutlet || loading) ? 0.6 : 1 }}
                                    disabled={!selectedOutlet || loading}
                                >
                                    {loading
                                        ? <Spinner size="sm" animation="border" style={{ width: 14, height: 14 }} />
                                        : '🔍'}
                                    {loading ? 'Generating…' : 'Generate report'}
                                </button>
                            </div>
                        </form>
                    </div>

                    {/* stat cards */}
                    {stats && (
                        <div style={s.statsRow}>
                            <StatCard label="Total out of stock" value={stats.total} accent="#e24b4a" sub={`${stats.categories} categories`} />
                            <StatCard label="Veg" value={stats.veg} accent="#639922" />
                            <StatCard label="Non-veg" value={stats.nonVeg} accent="#d85a30" />
                            <StatCard label="Food" value={stats.food} accent="#378add" />
                            <StatCard label="Beverages" value={stats.beverage} accent="#7f77dd" />
                            <StatCard label="Combos" value={stats.combos} accent="#ba7517" />
                            <StatCard label="Desserts" value={stats.desserts} accent="#d4537e" />
                        </div>
                    )}

                    {/* table */}
                    {reportData.length > 0 && (
                        <div style={cardStyle({ ...s.tableCard })}>
                            <div style={s.tableHdr}>
                                <div>
                                    <div style={{ ...s.tableTitle, color: dm('#1a1f36', '#f9fafb') }}>
                                        {outletName} — Exhausted products
                                    </div>
                                    <div style={s.tableSub}>
                                        Showing {filteredTotal} item{filteredTotal !== 1 ? 's' : ''} · {filteredData.length} categor{filteredData.length !== 1 ? 'ies' : 'y'}
                                    </div>
                                </div>
                                <div style={s.actions}>
                                    {['ALL', 'VEG', 'NON-VEG'].map(f => (
                                        <button
                                            key={f}
                                            style={s.pill(filterType === f)}
                                            onClick={() => setFilterType(f)}
                                        >
                                            {f === 'ALL' ? 'All' : f === 'VEG'
                                                ? <><span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: '#639922', marginRight: 3, verticalAlign: 'middle' }} />Veg</>
                                                : <><span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: '#d85a30', marginRight: 3, verticalAlign: 'middle' }} />Non-veg</>
                                            }
                                        </button>
                                    ))}
                                    <button style={s.exportBtn} onClick={handleExport} disabled={downloadBtnLoading}>
                                        <FileEarmarkSpreadsheet size={12} />
                                        {downloadBtnLoading
                                            ? <Spinner size="sm" animation="border" style={{ width: 12, height: 12 }} />
                                            : 'Excel'}
                                    </button>
                                </div>
                            </div>

                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, tableLayout: 'fixed' }}>
                                    <colgroup>
                                        <col style={{ width: 28 }} />
                                        <col style={{ width: '32%' }} />
                                        <col />
                                        <col style={{ width: 68 }} />
                                        <col style={{ width: 68 }} />
                                    </colgroup>
                                    <thead style={s.thead}>
                                        <tr>
                                            {['#', 'Product', 'Description', 'Class', 'Diet'].map(h => (
                                                <th key={h} style={s.th}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredData.map((category, ci) => (
                                            <React.Fragment key={category._id}>
                                                <tr style={{ ...s.catRow, background: dm('#f8f9ff', '#22294a') }}>
                                                    <td colSpan={5} style={{ ...s.catCell, color: dm('#2d3561', '#a5b4fc') }}>
                                                        📁 {category.categoryName}
                                                        <span style={s.catBadge}>{category.products.length}</span>
                                                    </td>
                                                </tr>
                                                {category.products.map((product, pi) => (
                                                    <tr
                                                        key={`${ci}-${pi}`}
                                                        style={{ background: dm(pi % 2 === 0 ? '#fff' : '#fafafa', pi % 2 === 0 ? '#1c2033' : '#20263d') }}
                                                    >
                                                        <td style={{ ...s.td, color: '#9ca3af', fontSize: 10 }}>{pi + 1}</td>
                                                        <td style={s.td}>
                                                            <div style={s.prodName}>{product.productName}</div>
                                                        </td>
                                                        <td style={{ ...s.td, color: '#6b7280', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {product.description}
                                                        </td>
                                                        <td style={s.td}><ClassBadge type={product.classType} /></td>
                                                        <td style={s.td}><FoodBadge type={product.foodType} /></td>
                                                    </tr>
                                                ))}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>

                                {filteredData.length === 0 && (
                                    <div style={s.emptyState}>
                                        <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
                                        <div style={{ fontWeight: 500, fontSize: 14, marginBottom: 4 }}>No items match this filter</div>
                                        <div style={{ fontSize: 12 }}>Try switching to a different filter above.</div>
                                    </div>
                                )}
                            </div>

                            <div style={{ ...s.tableFooter, borderTop: `0.5px solid ${dm('#f3f4f6', '#2a2f4a')}` }}>
                                Report generated {new Date().toLocaleString()} · {outletName}
                            </div>
                        </div>
                    )}

                    {/* pre-search empty state */}
                    {reportData.length === 0 && !loading && (
                        <div style={cardStyle({ ...s.tableCard, padding: '48px 24px', textAlign: 'center' })}>
                            <div style={{ fontSize: 36, marginBottom: 10 }}>📦</div>
                            <div style={{ fontWeight: 500, fontSize: 15, color: dm('#1a1f36', '#f9fafb'), marginBottom: 6 }}>
                                Select an outlet to see the products which are out of stock.
                            </div>
                            <div style={{ fontSize: 12, color: '#9ca3af' }}>
                                Choose an outlet above and click <strong>Generate report</strong> to see exhausted products.
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </React.Fragment>
    );
};

export default ProductStockReport;